/**
 * PersonalCalendar — Calendario unificado de actividades y contenidos.
 * 
 * Permite visualizar en una sola vista mensual o semanal:
 * - Publicaciones y contenidos de todos los clientes (o filtrado por cliente)
 * - Tareas personales con fecha límite
 * - Filtros por cliente y tipo de contenido
 */

import { useEffect, useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Calendar as CalendarIcon,
  Filter,
  Plus,
  Clock,
  Loader2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import type { Client, Task, ContentCalendarItem } from '@/types';

const CONTENT_TYPE_COLORS: Record<string, string> = {
  post: '#6366f1',
  reel: '#ec4899',
  story: '#f59e0b',
  carousel: '#22c55e',
  live: '#ef4444',
  blog: '#3b82f6',
  ad: '#8b5cf6',
  task: '#06b6d4',
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
  post: 'Post',
  reel: 'Reel',
  story: 'Story',
  carousel: 'Carrusel',
  live: 'En Vivo',
  blog: 'Blog',
  ad: 'Anuncio',
  task: 'Tarea',
};

export function PersonalCalendar() {
  const { currentPeriod } = useAppStore();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [calendarItems, setCalendarItems] = useState<(ContentCalendarItem & { clientName?: string })[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal de nuevo evento / tarea rápida
  const [showModal, setShowModal] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskClient, setNewTaskClient] = useState('');
  const [newTaskDate, setNewTaskDate] = useState(new Date().toISOString().slice(0, 10));
  const [newTaskPriority, setNewTaskPriority] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);

  // Modal detalle de evento
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [clientsRes, tasksRes] = await Promise.all([
          api.getClients(),
          api.getTasks(),
        ]);
        const clientList = clientsRes.clients as Client[];
        setClients(clientList);
        setTasks(tasksRes.tasks as Task[]);

        // Cargar calendarios de contenidos de todos los clientes activos
        const calendarPromises = clientList.map(async (c) => {
          try {
            const res = await api.getContentCalendar(c.id, currentPeriod);
            return (res.calendar || []).map((item: ContentCalendarItem) => ({
              ...item,
              clientName: c.name,
            }));
          } catch {
            return [];
          }
        });

        const calendarsNested = await Promise.all(calendarPromises);
        setCalendarItems(calendarsNested.flat());
      } catch (err) {
        console.error('Error cargando calendario general:', err);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [currentPeriod]);

  // Eventos unificados para FullCalendar
  const calendarEvents = useMemo(() => {
    const events: any[] = [];

    // Contenidos de clientes
    calendarItems.forEach((item) => {
      if (selectedClientId !== 'all' && item.client_id !== selectedClientId) return;
      if (selectedType !== 'all' && item.content_type !== selectedType) return;

      events.push({
        id: `content-${item.id}`,
        title: `${item.clientName ? `[${item.clientName}] ` : ''}${item.title}`,
        start: item.scheduled_date,
        backgroundColor: CONTENT_TYPE_COLORS[item.content_type] || '#6366f1',
        borderColor: CONTENT_TYPE_COLORS[item.content_type] || '#6366f1',
        extendedProps: {
          type: item.content_type,
          platform: item.platform,
          caption: item.caption,
          status: item.status,
          clientName: item.clientName,
        },
      });
    });

    // Tareas con fecha límite
    tasks.forEach((t) => {
      if (selectedType !== 'all' && selectedType !== 'task') return;
      if (selectedClientId !== 'all' && t.client_id !== selectedClientId) return;
      if (!t.due_date) return;

      events.push({
        id: `task-${t.id}`,
        title: `✓ ${t.client_name ? `[${t.client_name}] ` : ''}${t.title}`,
        start: t.due_date,
        backgroundColor: t.status === 'completed' ? '#94a3b8' : CONTENT_TYPE_COLORS.task,
        borderColor: t.status === 'completed' ? '#94a3b8' : CONTENT_TYPE_COLORS.task,
        extendedProps: {
          type: 'task',
          priority: t.priority,
          status: t.status,
          clientName: t.client_name,
          description: t.description,
        },
      });
    });

    return events;
  }, [calendarItems, tasks, selectedClientId, selectedType]);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle) return;

    setSubmitting(true);
    try {
      const res = await api.createTask({
        title: newTaskTitle,
        client_id: newTaskClient || null,
        due_date: newTaskDate,
        priority: newTaskPriority,
        category: newTaskClient ? 'client' : 'personal',
        status: 'pending',
      });

      const clientObj = clients.find((c) => c.id === newTaskClient);
      setTasks((prev) => [
        {
          id: res.id,
          title: newTaskTitle,
          client_id: newTaskClient || null,
          client_name: clientObj?.name,
          due_date: newTaskDate,
          priority: newTaskPriority as any,
          category: newTaskClient ? 'client' : 'personal',
          status: 'pending',
          completed_at: null,
          description: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        ...prev,
      ]);

      setNewTaskTitle('');
      setShowModal(false);
    } catch (err) {
      console.error('Error creando tarea:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEventClick = (info: any) => {
    const ext = info.event.extendedProps;
    setSelectedEvent({
      id: info.event.id,
      title: info.event.title,
      start: info.event.startStr,
      ...ext,
    });
  };

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CalendarIcon className="text-primary" />
            Calendario General
          </h1>
          <p className="text-muted-foreground text-sm">
            Vista unificada de publicaciones, entregas de contenido y tareas del mes.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => setShowModal(true)} className="gap-2">
            <Plus size={16} />
            Nueva Tarea
          </Button>
        </div>
      </div>

      {/* ─── Filtros y Leyenda ─── */}
      <Card>
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter size={16} />
              <span>Filtrar por:</span>
            </div>

            {/* Selector de Cliente */}
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              <option value="all">Todos los Clientes</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            {/* Selector de Tipo */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              <option value="all">Todos los Formatos</option>
              {Object.entries(CONTENT_TYPE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Leyenda rápida */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {Object.entries(CONTENT_TYPE_COLORS).map(([type, color]) => (
              <span key={type} className="flex items-center gap-1 font-medium text-muted-foreground">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: color }}
                />
                {CONTENT_TYPE_LABELS[type] || type}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ─── Calendario FullCalendar ─── */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          {loading ? (
            <div className="flex h-96 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek',
              }}
              buttonText={{
                today: 'Hoy',
                month: 'Mes',
                week: 'Semana',
              }}
              locale="es"
              events={calendarEvents}
              eventClick={handleEventClick}
              height="auto"
              dayMaxEvents={3}
            />
          )}
        </CardContent>
      </Card>

      {/* ─── Modal Detalle de Evento ─── */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl border animate-in">
            <div className="flex items-center justify-between pb-3 border-b">
              <Badge
                style={{
                  backgroundColor: CONTENT_TYPE_COLORS[selectedEvent.type] || '#6366f1',
                  color: '#fff',
                }}
              >
                {CONTENT_TYPE_LABELS[selectedEvent.type] || selectedEvent.type}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedEvent(null)}
                className="h-8 w-8"
              >
                <X size={16} />
              </Button>
            </div>

            <div className="mt-4 space-y-3">
              <h3 className="text-lg font-bold">{selectedEvent.title}</h3>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock size={12} /> Fecha programada: {selectedEvent.start}
              </p>

              {selectedEvent.clientName && (
                <p className="text-sm">
                  <span className="font-semibold text-muted-foreground">Cliente: </span>
                  {selectedEvent.clientName}
                </p>
              )}

              {selectedEvent.platform && (
                <p className="text-sm">
                  <span className="font-semibold text-muted-foreground">Plataforma: </span>
                  <span className="capitalize">{selectedEvent.platform}</span>
                </p>
              )}

              {selectedEvent.caption && (
                <div className="rounded-lg bg-muted p-3 text-xs">
                  <p className="font-medium text-muted-foreground mb-1">Caption / Nota:</p>
                  <p className="whitespace-pre-line text-foreground">{selectedEvent.caption}</p>
                </div>
              )}

              {selectedEvent.description && (
                <div className="rounded-lg bg-muted p-3 text-xs">
                  <p className="font-medium text-muted-foreground mb-1">Descripción:</p>
                  <p className="whitespace-pre-line text-foreground">{selectedEvent.description}</p>
                </div>
              )}

              <div className="pt-2 flex justify-end">
                <Button variant="outline" size="sm" onClick={() => setSelectedEvent(null)}>
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal Nueva Tarea Rápida ─── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl border animate-in">
            <div className="flex items-center justify-between pb-3 border-b">
              <h2 className="text-lg font-bold">Nueva Tarea en Calendario</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowModal(false)}
                className="h-8 w-8"
              >
                <X size={16} />
              </Button>
            </div>

            <form onSubmit={handleCreateTask} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Título de la tarea
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Grabar reel promocional..."
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Cliente (opcional)
                  </label>
                  <select
                    value={newTaskClient}
                    onChange={(e) => setNewTaskClient(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Personal (Sin cliente)</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Fecha límite
                  </label>
                  <input
                    type="date"
                    required
                    value={newTaskDate}
                    onChange={(e) => setNewTaskDate(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                  </input>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Prioridad
                </label>
                <select
                  value={newTaskPriority}
                  onChange={(e) => setNewTaskPriority(Number(e.target.value))}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value={0}>Baja</option>
                  <option value={1}>Media</option>
                  <option value={2}>Alta</option>
                  <option value={3}>Urgente</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : null}
                  Guardar Tarea
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
